/**
 * Axios HTTP Client Configuration
 *
 * This file contains configuration for the axios HTTP client, including:
 * - URL path exceptions for automatic redirects on 402 (Payment Required) errors
 * - Public routes that don't require authentication or subscription checks
 */

/**
 * Public routes that should NOT trigger authentication or subscription checks
 *
 * These routes are accessible without authentication:
 * - / - Login page
 * - /signup - Brand self sign-up (redirects to / while the sign-up is closed)
 * - /not-found - 404 error page
 *
 * Note: Keep this list in sync with SUBSCRIPTION_CHECK_EXEMPT_ROUTES in SubscriptionGuard
 */
export const PUBLIC_ROUTES = ['/', '/signup', '/not-found', '/team/join'] as const;

/**
 * Paths that should NOT trigger automatic redirect to paywall on 402 errors
 *
 * These paths are typically pages where:
 * - Users are in the process of setting up billing (paywall, onboarding)
 * - Users are waiting for async operations like webhook processing (welcome)
 * - Subscription status is checked but redirect would create a loop
 *
 * Note: This works alongside SubscriptionGuard as a second layer of protection.
 * SubscriptionGuard prevents rendering of protected routes without subscription.
 * This config prevents 402 redirects for specific flows (onboarding, welcome, etc.)
 *
 * @example
 * // User flow for Shopify billing:
 * // 1. /dashboard?paywall=plans - Choose plan (no redirect if 402)
 * // 2. /onboarding/shopify - Connect Shopify (no redirect if 402)
 * // 3. Shopify billing confirmation
 * // 4. /welcome/shopify - Wait for webhook (no redirect if 402)
 * // 5. /dashboard - Protected by SubscriptionGuard, will redirect if 402
 */
/** Existing money and its documents remain accessible under their own permissions. */
export const BILLING_FINANCIAL_ROUTES = [
  '/collabs/suivi',
  // The UGC page holds funded collabs the same way: their money outlives a plan.
  '/collabs/ugc',
  '/settings/billing',
  '/settings/invoices',
  '/payments/commissions',
  '/payout',
] as const;

export const isBillingFinancialRoute = (pathname: string): boolean =>
  BILLING_FINANCIAL_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

/** Personal identity and security remain reachable without opening organization settings. */
const BILLING_PERSONAL_ACCOUNT_ROUTES = [
  '/settings/account/profile',
  '/settings/account/security',
] as const;

export const PAYWALL_REDIRECT_EXCEPTIONS = [
  '/onboarding',
  '/welcome',
  ...BILLING_FINANCIAL_ROUTES,
  '/mcp/authorize',
  '/settings/account/assistants',
] as const;

/**
 * Configuration for axios interceptor behavior
 */
export const axiosConfig = {
  /**
   * Paths excluded from automatic 402 redirect
   */
  paywallRedirectExceptions: PAYWALL_REDIRECT_EXCEPTIONS,

  /**
   * Check if a given path should be excluded from automatic 402 redirect
   *
   * A free organization is never redirected, whatever the path: it has no
   * subscription to lapse and owes no Shopify store, so a 402 it meets is a
   * limit of its plan, answered on the page where it happened.
   *
   * @param pathname - The current window.location.pathname
   * @param plan - Whether the organization of the signed-in user is on the free plan
   * @returns true if the path should NOT redirect to paywall
   *
   * @example
   * if (shouldSkipPaywallRedirect('/welcome/shopify')) {
   *   // Don't redirect - user is on welcome page
   * }
   */
  shouldSkipPaywallRedirect: (pathname: string, plan: { isFreePlan?: boolean } = {}): boolean => {
    return (
      plan.isFreePlan === true ||
      BILLING_PERSONAL_ACCOUNT_ROUTES.some((route) => pathname === route) ||
      PAYWALL_REDIRECT_EXCEPTIONS.some(
        (exception) => pathname === exception || pathname.startsWith(`${exception}/`)
      )
    );
  },
} as const;

/**
 * Type-safe list of exception paths
 */
export type PaywallRedirectException = (typeof PAYWALL_REDIRECT_EXCEPTIONS)[number];
